import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  PatchAdminV1StoresStoreIdCustomersIdAddressesAddressIdBody,
  PostAdminV1StoresStoreIdCustomersIdAddressesBody,
} from '@nucleus-commerce/api-client/admin';

import { customersApi } from '../api/customers-api';

import { customerKeys } from './customer-keys';

/** A default change also moves the customer's `defaultAddressId`, so the detail refetches too. */
function useRefreshAddressBook(storeId: string, customerId: string) {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: customerKeys.addresses(storeId, customerId) }),
      queryClient.invalidateQueries({
        queryKey: customerKeys.detail(storeId, customerId),
        exact: true,
      }),
      queryClient.invalidateQueries({ queryKey: customerKeys.timeline(storeId, customerId) }),
    ]);
}

/** Field errors (`body.line1`, `body.region`) belong to the dialog, so the toast stays quiet. */
export function useCreateCustomerAddress(storeId: string, customerId: string) {
  const refresh = useRefreshAddressBook(storeId, customerId);
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdCustomersIdAddressesBody) =>
      customersApi.createAddress(storeId, customerId, body),
    meta: { successMessage: 'customers:toasts.addressAdded', silentError: true },
    onSuccess: refresh,
  });
}

/** The dialog renders its own errors; the one-click default switch on the card lets them toast. */
export function useUpdateCustomerAddress(
  storeId: string,
  customerId: string,
  meta: { successMessage: string; silentError: boolean } = {
    successMessage: 'customers:toasts.addressUpdated',
    silentError: true,
  },
) {
  const refresh = useRefreshAddressBook(storeId, customerId);
  return useMutation({
    mutationFn: ({
      addressId,
      body,
    }: {
      addressId: string;
      body: PatchAdminV1StoresStoreIdCustomersIdAddressesAddressIdBody;
    }) => customersApi.updateAddress(storeId, customerId, addressId, body),
    meta,
    onSuccess: refresh,
  });
}

export function useDeleteCustomerAddress(storeId: string, customerId: string) {
  const refresh = useRefreshAddressBook(storeId, customerId);
  return useMutation({
    mutationFn: (addressId: string) => customersApi.deleteAddress(storeId, customerId, addressId),
    meta: { successMessage: 'customers:toasts.addressDeleted' },
    onSuccess: refresh,
  });
}
